import React from "react";
import { Typography } from "@mui/material";
import { useMemo } from "react";
import ISODateTime from "./ISODateTime";
import { useTranslation } from "react-i18next";

export interface AutoTimestampDisplayProps {
  value: string;
  hideTime?: boolean;
  blankIsNow?: boolean;
}

export function AutoTimestampDisplay(props: AutoTimestampDisplayProps): JSX.Element {
  const { i18n } = useTranslation();

  const valueStr = props.value as string;
  // `Date.parse` works for our date strings because we use ISO-8601 format dates.  If parsing
  // the date fails, we are looking at a duration instead of date/time.
  const relativeDateString = useMemo(() => {
    if (!Date.parse(valueStr) && (valueStr.includes("P") || valueStr.includes("T"))) {
      const dateTimeKeys: Record<string, string> = {
        PY: "year",
        PM: "month",
        PW: "week",
        PD: "day",
        TH: "hour",
        TM: "minute",
        TS: "second"
      };
      const relativeEncodedString = props.value as string;
      const isPast = relativeEncodedString.startsWith("-");
      const parts = [];
      let valueString = "";
      let designator = "";
      let interval = "";
      let i = isPast ? 1 : 0;
      // Start parsing the date string.  We start with something like "-P1Y2M3DT4H5M6S".
      for ( ; i < relativeEncodedString.length; i++) {
        const char = relativeEncodedString[i];
        // First determine what part of the duration we are working with.  "P" is the date, "T"
        // is the time.
        if (char === "P" || char === "T") {
          designator = char;
          continue;
        }
        // Then parse the value.
        const charCode = relativeEncodedString.charCodeAt(i);
        const isInteger = 48 <= charCode && charCode <= 57;
        if (isInteger) {
          valueString += char;
          continue;
        }
        // Lastly, what we are left with is the date or time interval.
        interval = char;

        // Now we have the designator ("P" or "T"), the value (e.g. "2"), and the interval (e.g.
        // "M").  We can build the current part.
        const value = parseInt(valueString);
        // The interval needs a little work.  We can't use a hardcoded value because it isn't
        // locale aware.  We need to determine whether the interval is plural and make a lookup key.
        const dateTimeKey = designator + interval;
        interval = dateTimeKeys[dateTimeKey]
        const isPlural = value > 1;
        // Now we can translate the interval.
        interval = i18n.t(isPlural ? interval+"s" : interval);
        // Combine the value and interval (e.g. "2 Months").
        parts.push(`${value} ${interval}`);
        // Reset and keep going until the end.
        valueString = interval = "";
      }
      // Assemble the full date and time string.  We also need to include a tense to know it is
      // past or present.
      const timeTense = isPast ? i18n.t("time_past") : i18n.t("time_future");
      const relativeDateString = `${parts.join(", ")} ${timeTense}`;
      return relativeDateString;
    }
  }, [props.value]);

  const isNow = valueStr === "now";

  if (relativeDateString) {
    return <Typography variant="h5">{relativeDateString}</Typography>;
  }

  if (props.value) {
    if (isNow) {
      return <Typography variant="h5">{i18n.t("now")}</Typography>;
    }
    return <ISODateTime typographyVariant="h5" hideTime={props.hideTime} isoString={props.value as string} useUTC={true} />;
  }
  
  if (!props.value && props.blankIsNow) {
    return <Typography variant="h5">{i18n.t("now")}</Typography>;
  }

  return <></>;
}